import React, { useState } from "react";
import { Check, Copy, Link2Off, PanelRight } from "lucide-react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import BuddyAvatar from "@/shared/avatar/BuddyAvatar";
import Logo from "@/shared/components/Logo";
import { StatusBadge } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { useExtensionLink } from "./api/links";
import { askExtension } from "./extension";

/* `/extension/:agentId` — an agent's Chrome extension link, for anyone it was shared
   with. Outside the sign-in gate: they have a Definable extension account, not a
   dashboard one. "Add to Chrome" hands the agent to the installed extension; without it,
   the steps to install it and the link to paste. */

const STEPS = [
  "Install the Definable extension in Chrome.",
  "Open this page again and press Add to Chrome — or paste this link into the extension.",
  "Sign in with your email, and say hello.",
];

const dots = (
  <div className="flex-1 flex items-center justify-center gap-1" aria-label="Loading">
    <span className="dot-1 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
    <span className="dot-2 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
    <span className="dot-3 w-1.5 h-1.5 rounded-full bg-[var(--text-tertiary)]" />
  </div>
);

export default function ExtensionLinkPage() {
  const { agentId = "" } = useParams();
  const link = useExtensionLink(agentId);
  const installed = useQuery({ queryKey: ["extension-installed"], queryFn: async () => !!(await askExtension({ ping: true }))?.ok, retry: false });
  const add = useMutation({ mutationFn: async () => !!(await askExtension({ add: agentId }))?.ok, meta: { silent: true } });
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="h-screen w-full bg-[var(--surface-sunk)] text-[var(--text-primary)] flex flex-col overflow-hidden">
      <header className="h-12 shrink-0 flex items-center gap-1.5 px-4 md:px-5">
        <Logo size={15} />
        <span className="text-[13.5px] font-semibold tracking-[-0.01em]">Definable</span>
      </header>
      {/* The dashboard's shell: the card in its grey tray (DESIGN.md §4) */}
      <div className="flex-1 min-h-0 flex flex-col mx-2 mb-2 p-1 rounded-[22px] bg-[var(--surface-2)] ring-1 ring-[var(--outline)]">
        <main className="flex-1 min-h-0 flex flex-col overflow-y-auto rounded-[18px] bg-[var(--canvas)] ring-1 ring-[var(--hairline)] shadow-lift">
          {link.isPending ? dots : link.error || !link.data ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 fade-in px-4">
              <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
                <Link2Off className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
              </div>
              <div className="text-[14px] font-medium tracking-[-0.005em]">This link doesn't work any more</div>
              <div className="text-[12.5px] text-[var(--text-tertiary)] max-w-[320px] text-center">
                The agent was deleted, or this isn't an agent's link.
              </div>
            </div>
          ) : (
            <div className="my-auto w-full max-w-[440px] mx-auto px-4 py-10 flex flex-col items-center text-center fade-in-up">
              <BuddyAvatar seed={link.data.agent.seed ?? link.data.agent.name} size={64} ring />
              <h1 className="m-0 mt-5 text-[22px] leading-[28px] font-semibold tracking-[-0.02em]">{link.data.agent.name}</h1>
              <p className="m-0 mt-1.5 text-[13.5px] leading-[1.5] text-[var(--text-tertiary)] max-w-[360px]">
                Chat with {link.data.agent.name} in Chrome's side panel. It can see and act on the sites you allow, and you
                can stop it any time.
              </p>
              {!link.data.live && <div className="mt-3"><StatusBadge tone="warn">Not answering yet</StatusBadge></div>}
              {add.data ? (
                <div className="mt-6 flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
                  <PanelRight className="w-[14px] h-[14px]" strokeWidth={1.75} />
                  Added. Open Definable from Chrome's toolbar to start.
                </div>
              ) : installed.data ? (
                <BrandButton className="mt-6 h-9 px-4" onClick={() => add.mutate()} disabled={add.isPending}>Add to Chrome</BrandButton>
              ) : (
                /* the settings tray: a white card of steps in a grey frame */
                <div className="mt-6 w-full rounded-[18px] bg-[var(--surface-2)] p-1 text-left">
                  <div className="px-3 pt-1 pb-2 text-[12px] text-[var(--text-tertiary)]">Three steps</div>
                  <ol className="m-0 p-0 list-none rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift divide-y divide-[var(--hairline)]">
                    {STEPS.map((step, i) => (
                      <li key={step} className="flex items-start gap-3 px-3.5 py-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                        <span className="w-5 h-5 mt-px shrink-0 rounded-full bg-[var(--surface-2)] flex items-center justify-center font-mono tabular-nums text-[11px] text-[var(--text-tertiary)]">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {add.data === false && (
                <p className="m-0 mt-3 text-[12.5px] text-[var(--danger)]">The extension didn't answer. Paste the link into it instead.</p>
              )}
              <GhostButton className="mt-4" onClick={copy}>
                {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
                {copied ? "Copied" : "Copy link"}
              </GhostButton>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
